import React, { useState, useEffect, useMemo } from 'react';
import {
  BehavioralDomainId,
  StrictnessTier,
  TargetItem,
  ScheduledStrictnessRule,
  TargetDocumentSync,
  PolicyRecommendation,
} from '../sync/target-types';
import {
  BEHAVIORAL_DOMAINS,
  getDomainMeta,
  validateDomainOrPackage,
} from '../sync/preset-catalog';
import { EncryptedTargetSyncManager } from '../sync/encrypted-target-sync';
import { PolicyRecommendationService } from '../sync/policy-recommendation-service';

export interface WebTargetStudioProps {
  userId?: string;
  deviceId?: string;
  userRole?: 'self' | 'clinician' | 'family_guardian';
  syncManager?: EncryptedTargetSyncManager;
  policyService?: PolicyRecommendationService;
}

const TIER_META: Record<
  StrictnessTier,
  { label: string; shortLabel: string; color: string; bg: string; icon: string; desc: string }
> = {
  softPause: {
    label: 'Tier 1 • Soft Pause (10s)',
    shortLabel: 'Soft Pause',
    color: '#16A34A',
    bg: '#E8F5E9',
    icon: '🌿',
    desc: '10-second mindful breathing pause before app opens',
  },
  reflectiveJournal: {
    label: 'Tier 2 • Reflective Journal',
    shortLabel: 'Journaling',
    color: '#0284C7',
    bg: '#E0F2FE',
    icon: '📝',
    desc: 'CBT cognitive reframing check-in and urge surfing prompt',
  },
  groundingExercise: {
    label: 'Tier 3 • Somatic Grounding',
    shortLabel: 'Grounding',
    color: '#D97706',
    bg: '#FEF3C7',
    icon: '🧘',
    desc: '3-minute 4-4-4 breathing mandala & somatic grounding',
  },
  coolingOffContract: {
    label: 'Tier 4 • Cooling-Off Contract',
    shortLabel: 'Cooling-Off',
    color: '#EA580C',
    bg: '#FFEDD5',
    icon: '⏳',
    desc: 'Time-locked delay (5-15 mins) with audio commitment',
  },
  hardLock: {
    label: 'Tier 5 • Hard Lockout',
    shortLabel: 'Hard Lock',
    color: '#DC2626',
    bg: '#FEE2E2',
    icon: '🔒',
    desc: 'Complete access block during scheduled risk hours',
  },
};

export const WebTargetStudio: React.FC<WebTargetStudioProps> = ({
  userId = 'client_usr_882',
  deviceId = 'web_portal_01',
  userRole = 'self',
  syncManager: externalSyncManager,
  policyService: externalPolicyService,
}) => {
  // 1. Initialize Sync Manager and Policy Recommendation Service
  const syncManager = useMemo(() => {
    return (
      externalSyncManager ||
      new EncryptedTargetSyncManager({
        userId,
        deviceId,
        role: userRole,
      })
    );
  }, [externalSyncManager, userId, deviceId, userRole]);

  const policyService = useMemo(() => {
    return externalPolicyService || new PolicyRecommendationService();
  }, [externalPolicyService]);

  // 2. React State
  const [doc, setDoc] = useState<TargetDocumentSync>(syncManager.getDocument());
  const [selectedDomainId, setSelectedDomainId] = useState<BehavioralDomainId>('financial_speculation');
  const [customDomainInput, setCustomDomainInput] = useState<string>('');
  const [customInputError, setCustomInputError] = useState<string | null>(null);
  const [syncStatus, setSyncStatus] = useState<'synced' | 'syncing' | 'offline'>('synced');
  const [bannerNotice, setBannerNotice] = useState<string | null>(null);
  const [pendingRecommendations, setPendingRecommendations] = useState<PolicyRecommendation[]>([]);
  const [activeRecModal, setActiveRecModal] = useState<PolicyRecommendation | null>(null);

  // Per-Target Custom Limits Override State
  const [editingTargetOverride, setEditingTargetOverride] = useState<TargetItem | null>(null);
  const [overrideInactivityInput, setOverrideInactivityInput] = useState<string>('');
  const [overrideContinuousInput, setOverrideContinuousInput] = useState<string>('');
  const [overrideError, setOverrideError] = useState<string | null>(null);

  // 3. Subscriptions to Sync and Policy Recommendation updates
  useEffect(() => {
    const unsubSync = syncManager.subscribe((newDoc) => {
      setDoc(newDoc);
    });

    const unsubRecs = policyService.subscribeToUserRecommendations(userId, (recs) => {
      const pending = recs.filter((r) => r.status === 'pending');
      setPendingRecommendations(pending);
    });

    syncManager.startLiveSync().catch(console.error);

    return () => {
      unsubSync();
      unsubRecs();
      syncManager.stopLiveSync();
    };
  }, [syncManager, policyService, userId]);

  const selectedMeta = getDomainMeta(selectedDomainId);

  // Filter targets for selected domain
  const domainTargets = useMemo(() => {
    return doc.targets.filter((t) => t.category === selectedDomainId);
  }, [doc.targets, selectedDomainId]);

  const presetTargets = useMemo(() => {
    return domainTargets.filter((t) => !t.isCustom);
  }, [domainTargets]);

  const customTargets = useMemo(() => {
    return domainTargets.filter((t) => t.isCustom);
  }, [domainTargets]);

  const selectedRule = useMemo(() => {
    return (
      doc.scheduledRules.find((r) => r.categoryId === selectedDomainId) || {
        categoryId: selectedDomainId,
        daytimeTier: selectedMeta.defaultDaytimeTier,
        nighttimeTier: selectedMeta.defaultNighttimeTier,
        nightStartHour: 22,
        nightEndHour: 6,
        allowOverride: true,
        inactivityTimeoutMinutes: 15,
        continuousAllowanceMinutes: 20,
      }
    );
  }, [doc.scheduledRules, selectedDomainId, selectedMeta]);

  const isCategoryMasterActive = useMemo(() => {
    return domainTargets.some((t) => t.enabled);
  }, [domainTargets]);

  // --- Handlers ---

  const handleToggleTarget = async (targetId: string) => {
    setSyncStatus('syncing');
    await syncManager.toggleTarget(targetId);
    setSyncStatus('synced');
  };

  const handleAddCustomDomain = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setCustomInputError(null);

    const validation = validateDomainOrPackage(customDomainInput);
    if (!validation.isValid) {
      setCustomInputError(validation.errorMessage || 'Invalid domain format.');
      return;
    }

    setSyncStatus('syncing');
    const result = await syncManager.addCustomTarget(selectedDomainId, customDomainInput);
    if (!result.success) {
      setCustomInputError(result.error || 'Failed to add target.');
      setSyncStatus('synced');
      return;
    }

    setCustomDomainInput('');
    setSyncStatus('synced');
    setBannerNotice(`✓ Added custom target "${result.target?.name}" to ${selectedMeta.displayName}`);
    setTimeout(() => setBannerNotice(null), 3000);
  };

  const handleRemoveCustomTarget = async (targetId: string, name: string) => {
    setSyncStatus('syncing');
    await syncManager.removeTarget(targetId);
    setSyncStatus('synced');
    setBannerNotice(`Removed target "${name}".`);
    setTimeout(() => setBannerNotice(null), 2500);
  };

  const handleMasterToggleDomain = async (enabled: boolean) => {
    setSyncStatus('syncing');
    await syncManager.setMasterCategoryEnabled(selectedDomainId, enabled);
    setSyncStatus('synced');
  };

  const handleDaytimeTierChange = async (tier: StrictnessTier) => {
    setSyncStatus('syncing');
    await syncManager.updateScheduledRule({
      categoryId: selectedDomainId,
      daytimeTier: tier,
    });
    setSyncStatus('synced');
  };

  const handleNighttimeTierChange = async (tier: StrictnessTier) => {
    setSyncStatus('syncing');
    await syncManager.updateScheduledRule({
      categoryId: selectedDomainId,
      nighttimeTier: tier,
    });
    setSyncStatus('synced');
  };

  const handleOverrideToggle = async (allow: boolean) => {
    setSyncStatus('syncing');
    await syncManager.updateScheduledRule({
      categoryId: selectedDomainId,
      allowOverride: allow,
    });
    setSyncStatus('synced');
  };

  const handleInactivityTimeoutChange = async (minutes: number) => {
    const clamped = Math.max(15, minutes);
    setSyncStatus('syncing');
    await syncManager.updateScheduledRule({
      categoryId: selectedDomainId,
      inactivityTimeoutMinutes: clamped,
    });
    setSyncStatus('synced');
  };

  const handleContinuousAllowanceChange = async (minutes: number) => {
    const clamped = Math.max(20, minutes);
    setSyncStatus('syncing');
    await syncManager.updateScheduledRule({
      categoryId: selectedDomainId,
      continuousAllowanceMinutes: clamped,
    });
    setSyncStatus('synced');
  };

  const handleOpenOverrideModal = (target: TargetItem) => {
    setEditingTargetOverride(target);
    setOverrideInactivityInput(
      target.inactivityTimeoutMinutes !== undefined ? String(target.inactivityTimeoutMinutes) : ''
    );
    setOverrideContinuousInput(
      target.continuousAllowanceMinutes !== undefined ? String(target.continuousAllowanceMinutes) : ''
    );
    setOverrideError(null);
  };

  const handleSaveTargetOverride = async () => {
    if (!editingTargetOverride) return;
    setOverrideError(null);

    let inactVal: number | undefined = undefined;
    let contVal: number | undefined = undefined;

    if (overrideInactivityInput.trim() !== '') {
      const parsed = parseInt(overrideInactivityInput, 10);
      if (isNaN(parsed) || parsed < 15) {
        setOverrideError('Inactivity timeout override must be at least 15 minutes.');
        return;
      }
      inactVal = parsed;
    }

    if (overrideContinuousInput.trim() !== '') {
      const parsed = parseInt(overrideContinuousInput, 10);
      if (isNaN(parsed) || parsed < 20) {
        setOverrideError('Continuous session allowance override must be at least 20 minutes.');
        return;
      }
      contVal = parsed;
    }

    setSyncStatus('syncing');
    await syncManager.updateTargetLimits(editingTargetOverride.id, {
      inactivityTimeoutMinutes: inactVal,
      continuousAllowanceMinutes: contVal,
    });
    setSyncStatus('synced');
    setEditingTargetOverride(null);
    setBannerNotice(`✓ Updated custom timeout limits for "${editingTargetOverride.name}".`);
    setTimeout(() => setBannerNotice(null), 3000);
  };

  const handleClearTargetOverride = async () => {
    if (!editingTargetOverride) return;
    setSyncStatus('syncing');
    await syncManager.updateTargetLimits(editingTargetOverride.id, {
      inactivityTimeoutMinutes: undefined,
      continuousAllowanceMinutes: undefined,
    });
    setSyncStatus('synced');
    setEditingTargetOverride(null);
    setBannerNotice(`Reset "${editingTargetOverride.name}" to inherit category limits.`);
    setTimeout(() => setBannerNotice(null), 2500);
  };

  const handleAcceptRecommendation = async (rec: PolicyRecommendation) => {
    setSyncStatus('syncing');
    const res = await policyService.acceptRecommendation(userId, rec.id, syncManager);
    setSyncStatus('synced');
    setActiveRecModal(null);
    if (res.success) {
      setBannerNotice(`✓ Clinician policy recommendation for ${getDomainMeta(rec.targetCategory).displayName} applied.`);
      setTimeout(() => setBannerNotice(null), 4000);
    }
  };

  const handleDeclineRecommendation = async (rec: PolicyRecommendation) => {
    await policyService.declineRecommendation(userId, rec.id);
    setActiveRecModal(null);
    setBannerNotice('Policy recommendation declined.');
    setTimeout(() => setBannerNotice(null), 3000);
  };

  const handleSimulateConflict = async () => {
    setSyncStatus('syncing');
    // Simulate remote device update with concurrent clock
    const simulatedRemoteDoc: TargetDocumentSync = {
      schemaVersion: 2,
      userId,
      version: doc.version + 1,
      vectorClock: {
        ...doc.vectorClock,
        ios_iphone15: (doc.vectorClock.ios_iphone15 || 0) + 2,
      },
      updatedAt: new Date(Date.now() + 1000).toISOString(),
      lastModifiedBy: 'self',
      deviceId: 'ios_iphone15',
      summary: doc.summary,
      targets: doc.targets.map((t) =>
        t.category === selectedDomainId && t.id.includes('binance')
          ? { ...t, enabled: !t.enabled, updatedAt: Date.now() + 500 }
          : t
      ),
      scheduledRules: doc.scheduledRules,
    };

    syncManager.handleIncomingRemoteDocument(simulatedRemoteDoc);
    setSyncStatus('synced');
    setBannerNotice('⚡ Simulated cross-device concurrent sync resolved automatically with LWW.');
    setTimeout(() => setBannerNotice(null), 3500);
  };

  const handleSeedDemoRecommendation = async () => {
    await policyService.proposeRecommendation({
      userId,
      proposerUid: 'clinician_care_lead',
      proposerName: 'Care Lead Practitioner',
      targetCategory: selectedDomainId,
      daytimeTier: 'reflectiveJournal',
      nighttimeTier: 'hardLock',
      nightStartHour: 21,
      nightEndHour: 7,
      allowOverride: false,
      customCbtPrompt: 'Reflect on your grounded priorities before opening high-friction apps.',
      rationale: `Clinical recommendation to reinforce ${selectedMeta.displayName} during late-night hours.`,
      recommendedTargets: [
        {
          name: `bybit.com`,
          packageOrDomain: `bybit.com`,
          isCustom: true,
          enabled: true,
        },
      ],
    });
    setBannerNotice('📤 Demo recommendation received from Care Team.');
    setTimeout(() => setBannerNotice(null), 3000);
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      {/* Banner / Notice Alert */}
      {bannerNotice && (
        <div
          style={{
            backgroundColor: '#ECFDF5',
            border: '1px solid #10B981',
            borderRadius: '12px',
            padding: '12px 20px',
            color: '#065F46',
            fontSize: '13px',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 2px 8px rgba(16, 185, 129, 0.1)',
          }}
        >
          <span>{bannerNotice}</span>
          <button
            onClick={() => setBannerNotice(null)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#065F46', fontWeight: 800 }}
          >
            ✕
          </button>
        </div>
      )}

      {/* Pending Care Team Recommendations Alert Bar */}
      {pendingRecommendations.length > 0 && (
        <div
          style={{
            backgroundColor: '#EFF6FF',
            border: '1px solid #3B82F6',
            borderRadius: '14px',
            padding: '16px 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '12px',
            boxShadow: '0 3px 12px rgba(59, 130, 246, 0.12)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div
              style={{
                backgroundColor: '#3B82F6',
                color: '#FFFFFF',
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '18px',
              }}
            >
              🩺
            </div>
            <div>
              <div style={{ fontSize: '14px', fontWeight: 700, color: '#1E3A8A' }}>
                Care Team Policy Recommendation ({pendingRecommendations.length} pending)
              </div>
              <div style={{ fontSize: '12px', color: '#3B82F6', marginTop: '2px' }}>
                {pendingRecommendations[0].proposerName} suggested target adjustments for{' '}
                <strong>{getDomainMeta(pendingRecommendations[0].targetCategory).displayName}</strong>.
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => setActiveRecModal(pendingRecommendations[0])}
              style={{
                backgroundColor: '#2563EB',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '12px',
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 2px 6px rgba(37, 99, 235, 0.25)',
              }}
            >
              Review & Respond
            </button>
          </div>
        </div>
      )}

      {/* Sync Status Header */}
      <div
        style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '16px',
          padding: '20px 24px',
          border: '1px solid #E2E8F0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '16px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div
            style={{
              backgroundColor: '#E8F5E9',
              color: '#2D5A27',
              borderRadius: '12px',
              width: '44px',
              height: '44px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '24px',
            }}
          >
            🎯
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#1E293B' }}>
              Web Target Configuration Studio
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '4px' }}>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '11px',
                  fontWeight: 700,
                  color: syncStatus === 'synced' ? '#16A34A' : '#D97706',
                }}
              >
                <span
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: syncStatus === 'synced' ? '#16A34A' : '#D97706',
                  }}
                />
                {syncStatus === 'synced' ? 'Encrypted Sync Active' : 'Syncing Vector Clocks...'}
              </span>
              <span style={{ fontSize: '11px', color: '#64748B' }}>• Rev v{doc.version}</span>
              <span style={{ fontSize: '11px', color: '#64748B' }}>
                • Devices: {Object.keys(doc.vectorClock).join(', ')}
              </span>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button
            onClick={handleSimulateConflict}
            style={{
              backgroundColor: '#F8FAFC',
              border: '1px solid #CBD5E1',
              borderRadius: '8px',
              padding: '8px 14px',
              fontSize: '12px',
              fontWeight: 600,
              color: '#334155',
              cursor: 'pointer',
            }}
            title="Simulate a concurrent modification from iOS device and resolve via LWW"
          >
            ⚡ Test LWW Conflict
          </button>
          <button
            onClick={handleSeedDemoRecommendation}
            style={{
              backgroundColor: '#F0FDF4',
              border: '1px solid #86EFAC',
              borderRadius: '8px',
              padding: '8px 14px',
              fontSize: '12px',
              fontWeight: 600,
              color: '#166534',
              cursor: 'pointer',
            }}
          >
            🩺 Propose Clinician Policy
          </button>
        </div>
      </div>

      {/* 9-Domain Responsive Grid Selector */}
      <div>
        <div style={{ fontSize: '13px', fontWeight: 700, color: '#64748B', marginBottom: '12px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
          9 Behavioral Wellness Domains
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: '14px',
          }}
        >
          {BEHAVIORAL_DOMAINS.map((domain) => {
            const isSelected = domain.id === selectedDomainId;
            const categoryTargets = doc.targets.filter((t) => t.category === domain.id);
            const activeCount = categoryTargets.filter((t) => t.enabled).length;
            const totalCount = categoryTargets.length;
            const rule = doc.scheduledRules.find((r) => r.categoryId === domain.id);
            const dayTier = rule?.daytimeTier || domain.defaultDaytimeTier;
            const nightTier = rule?.nighttimeTier || domain.defaultNighttimeTier;

            return (
              <div
                key={domain.id}
                onClick={() => setSelectedDomainId(domain.id)}
                style={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '14px',
                  padding: '16px',
                  border: `2px solid ${isSelected ? domain.accentColor : '#E2E8F0'}`,
                  boxShadow: isSelected
                    ? `0 4px 16px ${domain.accentColor}25`
                    : '0 2px 6px rgba(0,0,0,0.02)',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  position: 'relative',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div
                      style={{
                        backgroundColor: domain.bgSoftColor,
                        borderRadius: '10px',
                        width: '36px',
                        height: '36px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '18px',
                      }}
                    >
                      {domain.icon}
                    </div>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 800, color: '#1E293B', lineHeight: 1.2 }}>
                        {domain.displayName}
                      </div>
                      <div style={{ fontSize: '11px', color: '#64748B', marginTop: '2px' }}>
                        {activeCount}/{totalCount} Active Targets
                      </div>
                    </div>
                  </div>

                  <span
                    style={{
                      backgroundColor: activeCount > 0 ? '#DCFCE7' : '#F1F5F9',
                      color: activeCount > 0 ? '#15803D' : '#64748B',
                      fontSize: '10px',
                      fontWeight: 700,
                      padding: '2px 8px',
                      borderRadius: '999px',
                    }}
                  >
                    {activeCount > 0 ? 'ON' : 'OFF'}
                  </span>
                </div>

                {/* Day / Night Strictness & Timeout Badges */}
                <div
                  style={{
                    display: 'flex',
                    gap: '6px',
                    backgroundColor: '#F8FAFC',
                    padding: '6px 8px',
                    borderRadius: '8px',
                    fontSize: '10px',
                    color: '#475569',
                    fontWeight: 600,
                    flexWrap: 'wrap',
                  }}
                >
                  <span title={`Daytime: ${TIER_META[dayTier].label}`}>
                    ☀️ {TIER_META[dayTier].shortLabel}
                  </span>
                  <span>•</span>
                  <span title={`Nighttime: ${TIER_META[nightTier].label}`}>
                    🌙 {TIER_META[nightTier].shortLabel}
                  </span>
                  <span>•</span>
                  <span title={`Inactivity Timeout: ${rule?.inactivityTimeoutMinutes ?? 15}m`}>
                    ⏱️ {rule?.inactivityTimeoutMinutes ?? 15}m
                  </span>
                  <span>•</span>
                  <span title={`Continuous Allowance: ${rule?.continuousAllowanceMinutes ?? 20}m`}>
                    ⏳ {rule?.continuousAllowanceMinutes ?? 20}m
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Selected Domain Configuration Studio Pane */}
      <div
        style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '16px',
          padding: '28px',
          border: '1px solid #E2E8F0',
          boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
        }}
      >
        {/* Domain Header and Master Switch */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
            paddingBottom: '20px',
            borderBottom: '1px solid #F1F5F9',
            flexWrap: 'wrap',
            gap: '16px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div
              style={{
                backgroundColor: selectedMeta.bgSoftColor,
                borderRadius: '14px',
                width: '50px',
                height: '50px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '26px',
              }}
            >
              {selectedMeta.icon}
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: '#1E293B' }}>
                {selectedMeta.displayName}
              </h3>
              <p style={{ margin: '3px 0 0', fontSize: '13px', color: '#64748B' }}>
                {selectedMeta.description}
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '13px', fontWeight: 600, color: '#475569' }}>
              Master Category Interception:
            </span>
            <button
              onClick={() => handleMasterToggleDomain(!isCategoryMasterActive)}
              style={{
                backgroundColor: isCategoryMasterActive ? selectedMeta.accentColor : '#CBD5E1',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '999px',
                padding: '8px 20px',
                fontSize: '12px',
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: isCategoryMasterActive ? `0 2px 8px ${selectedMeta.accentColor}40` : 'none',
                transition: 'all 0.15s ease',
              }}
            >
              {isCategoryMasterActive ? '✓ Category Active' : '✕ Disabled'}
            </button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '28px' }}>
          {/* Left Column: Preset App Chips and Custom Domain Editor */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* Active Preset App Chips */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <label style={{ fontSize: '13px', fontWeight: 700, color: '#1E293B' }}>
                  Curated Preset Protection Targets
                </label>
                <span style={{ fontSize: '11px', color: '#64748B' }}>
                  Tap chip to toggle • ⚙️ for custom limits
                </span>
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                {presetTargets.map((target) => {
                  const hasCustomLimits =
                    target.inactivityTimeoutMinutes !== undefined || target.continuousAllowanceMinutes !== undefined;
                  return (
                    <div
                      key={target.id}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        borderRadius: '10px',
                        border: `1px solid ${target.enabled ? selectedMeta.accentColor : '#CBD5E1'}`,
                        backgroundColor: target.enabled ? selectedMeta.bgSoftColor : '#FFFFFF',
                        overflow: 'hidden',
                      }}
                    >
                      <button
                        onClick={() => handleToggleTarget(target.id)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '8px 12px',
                          border: 'none',
                          background: 'none',
                          color: target.enabled ? '#0F172A' : '#64748B',
                          fontSize: '12px',
                          fontWeight: 700,
                          cursor: 'pointer',
                        }}
                      >
                        <span
                          style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: target.enabled ? selectedMeta.accentColor : '#94A3B8',
                          }}
                        />
                        {target.name}
                        {hasCustomLimits && (
                          <span
                            style={{
                              backgroundColor: '#EFF6FF',
                              color: '#1D4ED8',
                              fontSize: '10px',
                              padding: '1px 5px',
                              borderRadius: '4px',
                              fontWeight: 700,
                            }}
                            title={`Custom Limits: ${target.inactivityTimeoutMinutes ?? 15}m inact / ${target.continuousAllowanceMinutes ?? 20}m cont`}
                          >
                            ⏱️ {target.inactivityTimeoutMinutes ?? 15}m/{target.continuousAllowanceMinutes ?? 20}m
                          </span>
                        )}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenOverrideModal(target);
                        }}
                        title="Configure custom timeout override for this target"
                        style={{
                          padding: '8px 8px',
                          borderLeft: `1px solid ${target.enabled ? selectedMeta.accentColor + '55' : '#E2E8F0'}`,
                          borderTop: 'none',
                          borderRight: 'none',
                          borderBottom: 'none',
                          background: 'transparent',
                          color: hasCustomLimits ? '#2563EB' : '#64748B',
                          fontSize: '11px',
                          cursor: 'pointer',
                          fontWeight: 700,
                        }}
                      >
                        ⚙️
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Custom Domain / Package Editor with Validation */}
            <div
              style={{
                backgroundColor: '#F8FAFC',
                borderRadius: '14px',
                padding: '18px',
                border: '1px solid #E2E8F0',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <label style={{ fontSize: '13px', fontWeight: 700, color: '#1E293B' }}>
                  🌐 Add Custom Domain / App Package
                </label>
                <span style={{ fontSize: '10px', color: '#059669', fontWeight: 700 }}>
                  🔒 Zero-Knowledge Encrypted
                </span>
              </div>
              <p style={{ fontSize: '12px', color: '#64748B', margin: '0 0 12px' }}>
                Enter custom websites (e.g. <code>dexscreener.com</code>) or Android package IDs (e.g. <code>com.bybit.app</code>).
              </p>

              <form onSubmit={handleAddCustomDomain} style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  placeholder="e.g. dexscreener.com or com.trading.app"
                  value={customDomainInput}
                  onChange={(e) => {
                    setCustomDomainInput(e.target.value);
                    setCustomInputError(null);
                  }}
                  style={{
                    flex: 1,
                    padding: '10px 14px',
                    borderRadius: '8px',
                    border: `1px solid ${customInputError ? '#EF4444' : '#CBD5E1'}`,
                    fontSize: '13px',
                    outline: 'none',
                    backgroundColor: '#FFFFFF',
                  }}
                />
                <button
                  type="submit"
                  style={{
                    backgroundColor: '#2D5A27',
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '10px 18px',
                    fontSize: '13px',
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  + Add Target
                </button>
              </form>

              {customInputError && (
                <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '6px', fontWeight: 600 }}>
                  ⚠ {customInputError}
                </div>
              )}

              {/* Custom Targets List */}
              {customTargets.length > 0 && (
                <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase' }}>
                    User-Added Custom Targets ({customTargets.length})
                  </div>
                  {customTargets.map((target) => {
                    const hasCustomLimits =
                      target.inactivityTimeoutMinutes !== undefined || target.continuousAllowanceMinutes !== undefined;
                    return (
                      <div
                        key={target.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '8px 12px',
                          backgroundColor: '#FFFFFF',
                          borderRadius: '8px',
                          border: '1px solid #CBD5E1',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <input
                            type="checkbox"
                            checked={target.enabled}
                            onChange={() => handleToggleTarget(target.id)}
                            style={{ cursor: 'pointer', accentColor: selectedMeta.accentColor }}
                          />
                          <span style={{ fontSize: '12px', fontWeight: 600, color: '#1E293B' }}>
                            {target.name}
                          </span>
                          <span style={{ fontSize: '10px', color: '#94A3B8' }}>
                            ({target.packageOrDomain})
                          </span>
                          {hasCustomLimits && (
                            <span
                              style={{
                                backgroundColor: '#EFF6FF',
                                color: '#1D4ED8',
                                fontSize: '10px',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                fontWeight: 700,
                              }}
                            >
                              ⏱️ Override: {target.inactivityTimeoutMinutes ?? 15}m inact / {target.continuousAllowanceMinutes ?? 20}m allow
                            </span>
                          )}
                        </div>

                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <button
                            onClick={() => handleOpenOverrideModal(target)}
                            style={{
                              backgroundColor: '#F1F5F9',
                              color: '#334155',
                              border: '1px solid #CBD5E1',
                              borderRadius: '6px',
                              padding: '4px 8px',
                              fontSize: '11px',
                              cursor: 'pointer',
                              fontWeight: 600,
                            }}
                            title="Configure custom timeout override"
                          >
                            ⚙️ Limits
                          </button>
                          <button
                            onClick={() => handleRemoveCustomTarget(target.id, target.name)}
                            style={{
                              backgroundColor: 'transparent',
                              color: '#EF4444',
                              border: 'none',
                              fontSize: '12px',
                              cursor: 'pointer',
                              fontWeight: 700,
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Right Column: Scheduled Day / Night Strictness Tier & Timeout Engine Selector */}
          <div
            style={{
              backgroundColor: '#F8FAFC',
              borderRadius: '16px',
              padding: '22px',
              border: '1px solid #E2E8F0',
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
            }}
          >
            <div>
              <h4 style={{ margin: '0 0 4px', fontSize: '15px', fontWeight: 800, color: '#1E293B' }}>
                ⏰ Strictness Tiers & Cooldown Engine
              </h4>
              <p style={{ margin: 0, fontSize: '12px', color: '#64748B' }}>
                Configure intervention intensity, tab inactivity returns, and continuous session allowance limits.
              </p>
            </div>

            {/* Daytime Strictness Tier */}
            <div>
              <label style={{ fontSize: '12px', fontWeight: 700, color: '#1E293B', display: 'block', marginBottom: '8px' }}>
                ☀️ Daytime Strictness Tier (06:00 – 22:00)
              </label>
              <select
                value={selectedRule.daytimeTier}
                onChange={(e) => handleDaytimeTierChange(e.target.value as StrictnessTier)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: '1px solid #CBD5E1',
                  backgroundColor: '#FFFFFF',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#1E293B',
                }}
              >
                {(Object.keys(TIER_META) as StrictnessTier[]).map((tier) => (
                  <option key={tier} value={tier}>
                    {TIER_META[tier].icon} {TIER_META[tier].label}
                  </option>
                ))}
              </select>
              <p style={{ fontSize: '11px', color: '#64748B', margin: '4px 0 0' }}>
                {TIER_META[selectedRule.daytimeTier].desc}
              </p>
            </div>

            {/* Nighttime Strictness Tier */}
            <div>
              <label style={{ fontSize: '12px', fontWeight: 700, color: '#1E293B', display: 'block', marginBottom: '8px' }}>
                🌙 Nighttime High-Risk Strictness Tier (22:00 – 06:00)
              </label>
              <select
                value={selectedRule.nighttimeTier}
                onChange={(e) => handleNighttimeTierChange(e.target.value as StrictnessTier)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: '1px solid #CBD5E1',
                  backgroundColor: '#FFFFFF',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#1E293B',
                }}
              >
                {(Object.keys(TIER_META) as StrictnessTier[]).map((tier) => (
                  <option key={tier} value={tier}>
                    {TIER_META[tier].icon} {TIER_META[tier].label}
                  </option>
                ))}
              </select>
              <p style={{ fontSize: '11px', color: '#64748B', margin: '4px 0 0' }}>
                {TIER_META[selectedRule.nighttimeTier].desc}
              </p>
            </div>

            {/* Inactivity & Continuous Browsing Allowance Sliders */}
            <div
              style={{
                backgroundColor: '#FFFFFF',
                borderRadius: '12px',
                padding: '16px',
                border: '1px solid #E2E8F0',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <label style={{ fontSize: '13px', fontWeight: 700, color: '#1E293B' }}>
                  ⏱️ Tab Inactivity & Session Allowance
                </label>
                <span style={{ fontSize: '10px', color: '#059669', fontWeight: 700, backgroundColor: '#ECFDF5', padding: '2px 6px', borderRadius: '6px' }}>
                  Anti-Bypass Cooldown
                </span>
              </div>

              {/* Inactivity Timeout Slider */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <span style={{ fontSize: '12px', fontWeight: 600, color: '#334155' }}>
                    Tab Inactivity Cooldown
                  </span>
                  <span
                    style={{
                      backgroundColor: '#EFF6FF',
                      color: '#1D4ED8',
                      fontSize: '11px',
                      fontWeight: 700,
                      padding: '2px 8px',
                      borderRadius: '999px',
                      border: '1px solid #BFDBFE',
                    }}
                  >
                    {selectedRule.inactivityTimeoutMinutes ?? 15} mins (min 15m)
                  </span>
                </div>
                <input
                  type="range"
                  min={15}
                  max={120}
                  step={5}
                  value={selectedRule.inactivityTimeoutMinutes ?? 15}
                  onChange={(e) => handleInactivityTimeoutChange(Number(e.target.value))}
                  style={{ width: '100%', accentColor: selectedMeta.accentColor, cursor: 'pointer' }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#94A3B8', marginTop: '2px' }}>
                  <span>15m (Strict)</span>
                  <span>60m</span>
                  <span>120m (Relaxed)</span>
                </div>
                <p style={{ fontSize: '11px', color: '#64748B', margin: '4px 0 0' }}>
                  Re-triggers the intervention modal immediately upon returning to an idle background tab.
                </p>
              </div>

              {/* Continuous Browsing Allowance Slider */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <span style={{ fontSize: '12px', fontWeight: 600, color: '#334155' }}>
                    Continuous Browsing Allowance
                  </span>
                  <span
                    style={{
                      backgroundColor: '#F0FDF4',
                      color: '#15803D',
                      fontSize: '11px',
                      fontWeight: 700,
                      padding: '2px 8px',
                      borderRadius: '999px',
                      border: '1px solid #BBF7D0',
                    }}
                  >
                    {selectedRule.continuousAllowanceMinutes ?? 20} mins (min 20m)
                  </span>
                </div>
                <input
                  type="range"
                  min={20}
                  max={180}
                  step={5}
                  value={selectedRule.continuousAllowanceMinutes ?? 20}
                  onChange={(e) => handleContinuousAllowanceChange(Number(e.target.value))}
                  style={{ width: '100%', accentColor: selectedMeta.accentColor, cursor: 'pointer' }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#94A3B8', marginTop: '2px' }}>
                  <span>20m (Mindful)</span>
                  <span>60m</span>
                  <span>180m (Extended)</span>
                </div>
                <p style={{ fontSize: '11px', color: '#64748B', margin: '4px 0 0' }}>
                  Presents a gentle reflective pause when uninterrupted browsing on target domains reaches this limit.
                </p>
              </div>
            </div>

            {/* Override Controls */}
            <div
              style={{
                backgroundColor: '#FFFFFF',
                borderRadius: '10px',
                padding: '14px',
                border: '1px solid #E2E8F0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div>
                <div style={{ fontSize: '12px', fontWeight: 700, color: '#1E293B' }}>
                  Emergency Override Permitted
                </div>
                <div style={{ fontSize: '11px', color: '#64748B', marginTop: '2px' }}>
                  Allows 60-second bypass with cognitive journaling check-in
                </div>
              </div>
              <input
                type="checkbox"
                checked={selectedRule.allowOverride}
                onChange={(e) => handleOverrideToggle(e.target.checked)}
                style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#2D5A27' }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Target-Specific Limit Override Modal */}
      {editingTargetOverride && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px',
          }}
        >
          <div
            style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '20px',
              maxWidth: '480px',
              width: '100%',
              padding: '28px',
              boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
              border: '1px solid #E2E8F0',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <div
                style={{
                  backgroundColor: '#EFF6FF',
                  color: '#2563EB',
                  borderRadius: '12px',
                  width: '42px',
                  height: '42px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '22px',
                }}
              >
                ⚙️
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#1E293B' }}>
                  Custom Cooldown Limits
                </h3>
                <span style={{ fontSize: '12px', color: '#64748B' }}>
                  {editingTargetOverride.name} ({editingTargetOverride.packageOrDomain})
                </span>
              </div>
            </div>

            <p style={{ fontSize: '13px', color: '#475569', lineHeight: 1.5, marginBottom: '18px' }}>
              Override category-wide defaults with custom inactivity timeout and session allowance specifically for this target.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '20px' }}>
              {/* Target Inactivity Override */}
              <div>
                <label style={{ fontSize: '12px', fontWeight: 700, color: '#1E293B', display: 'block', marginBottom: '4px' }}>
                  Inactivity Timeout Override (Minutes, Min 15m)
                </label>
                <input
                  type="number"
                  min={15}
                  placeholder={`Category default (${selectedRule.inactivityTimeoutMinutes ?? 15}m)`}
                  value={overrideInactivityInput}
                  onChange={(e) => setOverrideInactivityInput(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: '1px solid #CBD5E1',
                    fontSize: '13px',
                    boxSizing: 'border-box',
                  }}
                />
                <span style={{ fontSize: '11px', color: '#94A3B8', marginTop: '2px', display: 'block' }}>
                  Leave blank to inherit category default ({selectedRule.inactivityTimeoutMinutes ?? 15}m).
                </span>
              </div>

              {/* Target Continuous Allowance Override */}
              <div>
                <label style={{ fontSize: '12px', fontWeight: 700, color: '#1E293B', display: 'block', marginBottom: '4px' }}>
                  Continuous Session Allowance Override (Minutes, Min 20m)
                </label>
                <input
                  type="number"
                  min={20}
                  placeholder={`Category default (${selectedRule.continuousAllowanceMinutes ?? 20}m)`}
                  value={overrideContinuousInput}
                  onChange={(e) => setOverrideContinuousInput(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: '1px solid #CBD5E1',
                    fontSize: '13px',
                    boxSizing: 'border-box',
                  }}
                />
                <span style={{ fontSize: '11px', color: '#94A3B8', marginTop: '2px', display: 'block' }}>
                  Leave blank to inherit category default ({selectedRule.continuousAllowanceMinutes ?? 20}m).
                </span>
              </div>

              {overrideError && (
                <div style={{ fontSize: '12px', color: '#DC2626', fontWeight: 600 }}>
                  ⚠ {overrideError}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'space-between', alignItems: 'center' }}>
              <button
                onClick={handleClearTargetOverride}
                style={{
                  backgroundColor: '#F1F5F9',
                  color: '#475569',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '10px 14px',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Reset to Default
              </button>

              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => setEditingTargetOverride(null)}
                  style={{
                    backgroundColor: '#F8FAFC',
                    color: '#64748B',
                    border: '1px solid #CBD5E1',
                    borderRadius: '8px',
                    padding: '10px 16px',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveTargetOverride}
                  style={{
                    backgroundColor: '#2D5A27',
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '10px 18px',
                    fontSize: '13px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(45, 90, 39, 0.25)',
                  }}
                >
                  Save Override
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Policy Recommendation Acceptance Modal */}
      {activeRecModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px',
          }}
        >
          <div
            style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '20px',
              maxWidth: '520px',
              width: '100%',
              padding: '28px',
              boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
              border: '1px solid #E2E8F0',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <div
                style={{
                  backgroundColor: '#EFF6FF',
                  color: '#2563EB',
                  borderRadius: '12px',
                  width: '42px',
                  height: '42px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '22px',
                }}
              >
                🩺
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#1E293B' }}>
                  Care Team Recommendation
                </h3>
                <span style={{ fontSize: '12px', color: '#64748B' }}>
                  Proposed by {activeRecModal.proposerName}
                </span>
              </div>
            </div>

            <p style={{ fontSize: '13px', color: '#475569', lineHeight: 1.5, marginBottom: '18px' }}>
              {activeRecModal.rationale}
            </p>

            <div
              style={{
                backgroundColor: '#F8FAFC',
                borderRadius: '12px',
                padding: '16px',
                border: '1px solid #E2E8F0',
                marginBottom: '20px',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
              }}
            >
              <div style={{ fontSize: '12px', fontWeight: 700, color: '#1E293B' }}>
                Proposed Adjustments for {getDomainMeta(activeRecModal.targetCategory).displayName}:
              </div>
              <div style={{ fontSize: '12px', color: '#334155' }}>
                ☀️ Daytime: <strong>{TIER_META[activeRecModal.daytimeTier].label}</strong>
              </div>
              <div style={{ fontSize: '12px', color: '#334155' }}>
                🌙 Nighttime: <strong>{TIER_META[activeRecModal.nighttimeTier].label}</strong>
              </div>
              {activeRecModal.inactivityTimeoutMinutes !== undefined && (
                <div style={{ fontSize: '12px', color: '#334155' }}>
                  ⏱️ Inactivity Timeout: <strong>{activeRecModal.inactivityTimeoutMinutes} minutes</strong>
                </div>
              )}
              {activeRecModal.continuousAllowanceMinutes !== undefined && (
                <div style={{ fontSize: '12px', color: '#334155' }}>
                  ⏳ Continuous Allowance: <strong>{activeRecModal.continuousAllowanceMinutes} minutes</strong>
                </div>
              )}
              {activeRecModal.recommendedTargets && activeRecModal.recommendedTargets.length > 0 && (
                <div style={{ fontSize: '12px', color: '#334155' }}>
                  🎯 Recommended Targets: <strong>{activeRecModal.recommendedTargets.map((t) => t.name).join(', ')}</strong>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => handleDeclineRecommendation(activeRecModal)}
                style={{
                  backgroundColor: '#F1F5F9',
                  color: '#475569',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '10px 18px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Decline
              </button>
              <button
                onClick={() => handleAcceptRecommendation(activeRecModal)}
                style={{
                  backgroundColor: '#2563EB',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '10px 20px',
                  fontSize: '13px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(37, 99, 235, 0.25)',
                }}
              >
                ✓ Accept & Synchronize
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebTargetStudio;
